import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import { bandMeta, formatScore, type BandShape } from '../lib/bands'
import { stalenessMessage } from '../lib/format'
import { Icon } from './Icon'

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

/* ---------- severity ---------- */

export function BandShapeIcon({ shape, size = 14 }: { shape: BandShape; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 16 16', 'aria-hidden': true, focusable: false, 'data-shape': shape } as const
  switch (shape) {
    case 'circle-check':
      return (<svg {...common}><circle cx="8" cy="8" r="7" fill="currentColor" /><path d="m4.6 8.3 2.2 2.2 4.6-4.8" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>)
    case 'triangle':
      return (<svg {...common}><path d="M8 1.5 15 14H1L8 1.5Z" fill="currentColor" /><path d="M8 6v4M8 11.6v.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" /></svg>)
    case 'diamond':
      return (<svg {...common}><path d="M8 .8 15.2 8 8 15.2.8 8 8 .8Z" fill="currentColor" /><path d="M8 4.6v4M8 10.2v.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" /></svg>)
    case 'octagon':
      return (<svg {...common}><path d="M5.2.8h5.6l4.4 4.4v5.6l-4.4 4.4H5.2L.8 10.8V5.2L5.2.8Z" fill="currentColor" /><path d="m5.6 5.6 4.8 4.8M10.4 5.6l-4.8 4.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" /></svg>)
    default:
      return (<svg {...common}><circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.4 2" /></svg>)
  }
}

export function BandBadge({ band, total, showScore, size = 'md' }: { band: string | null | undefined; total?: string | null; showScore?: boolean; size?: 'sm' | 'md' | 'lg' }) {
  const meta = bandMeta(band)
  const withScore = showScore || total !== undefined
  return (
    <span className={cx('band-badge', `band-badge--${size}`)} data-band={meta.code} style={{ color: meta.ink, background: meta.soft, borderColor: meta.color }}>
      <span style={{ color: meta.color, display: 'inline-flex' }}><BandShapeIcon shape={meta.shape} /></span>
      <span className="band-badge__label">{meta.label}</span>
      {withScore ? <span className="band-badge__score mono">{formatScore(total)}</span> : null}
    </span>
  )
}

export type Tone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger' | 'off'

export function StatusBadge({ label, tone = 'neutral', icon }: { label: string; tone?: Tone; icon?: Parameters<typeof Icon>[0]['name'] }) {
  return (
    <span className="status-badge" data-tone={tone}>
      {icon ? <Icon name={icon} size={13} /> : null}
      {label}
    </span>
  )
}

export function RouteCriticalBadge() {
  return (
    <span className="status-badge" data-tone="info" title="Señalado como de ruta crítica en el informe de diciembre de 2024">
      <Icon name="flag" size={13} />
      Ruta crítica
    </span>
  )
}

export function StalenessBanner({ inspectedOn }: { inspectedOn: string | null | undefined }) {
  return (
    <p role="note" className="stale-banner">
      <Icon name="history" size={16} />
      <span>{stalenessMessage(inspectedOn)}</span>
    </p>
  )
}

/* ---------- states ---------- */

export function LoadingState({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div role="status" className="state-inline">
      <span className="spinner" aria-hidden="true" />
      {label}
    </div>
  )
}

export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const api = error instanceof ApiError ? error : null
  const message = api?.status === 0 ? 'No se puede conectar con el servidor. Revisar la conexión e intentar de nuevo.' : api?.message ?? 'Algo salió mal.'
  return (
    <div role="alert" className="state-inline state-inline--error">
      <Icon name="triangle-alert" size={20} />
      <div>
        {title ? <strong>{title}</strong> : null}
        <div>{message}</div>
      </div>
      {onRetry ? (
        <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
          <Icon name="refresh" size={14} /> Reintentar
        </button>
      ) : null}
    </div>
  )
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <div className="empty-state__mark" aria-hidden="true"><Icon name="info" size={22} /></div>
      <strong>{title}</strong>
      {hint ? <p className="muted">{hint}</p> : null}
      {action}
    </div>
  )
}

/* ---------- controls ---------- */

export function Segmented<T extends string>({ label, value, onChange, options, small }: { label: string; value: T; onChange: (value: T) => void; options: { value: T; label: string }[]; small?: boolean }) {
  const group = useId()
  const refs = useRef<(HTMLInputElement | null)[]>([])
  function onKey(event: KeyboardEvent<HTMLDivElement>) {
    const index = options.findIndex((o) => o.value === value)
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault()
      const next = (index + 1) % options.length
      onChange(options[next].value)
      refs.current[next]?.focus()
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault()
      const next = (index - 1 + options.length) % options.length
      onChange(options[next].value)
      refs.current[next]?.focus()
    }
  }
  return (
    <div role="radiogroup" aria-label={label} className={cx('segmented', small && 'segmented--sm')} onKeyDown={onKey}>
      {options.map((option, i) => (
        <label key={option.value} className="segmented__item">
          <input
            ref={(el) => { refs.current[i] = el }}
            type="radio"
            name={group}
            value={option.value}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
            tabIndex={option.value === value ? 0 : -1}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  )
}

export function PillFilter({ active, onClick, children, count }: { active: boolean; onClick: () => void; children: ReactNode; count?: number }) {
  return (
    <button type="button" className={cx('pill', active && 'pill--active')} aria-pressed={active} onClick={onClick}>
      {children}
      {count !== undefined ? <span className="pill__count mono">{count}</span> : null}
    </button>
  )
}

export function Pagination({ page, pageSize, count, onPage }: { page: number; pageSize: number; count: number; onPage: (page: number) => void }) {
  if (count <= pageSize) return null
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, count)
  const last = Math.ceil(count / pageSize)
  return (
    <nav className="pagination" aria-label="Paginación">
      <span className="muted mono">{`${from}–${to} de ${count}`}</span>
      <button type="button" className="btn btn--ghost btn--sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        <Icon name="chevron-left" size={14} /> Anterior
      </button>
      <button type="button" className="btn btn--ghost btn--sm" disabled={page >= last} onClick={() => onPage(page + 1)}>
        Siguiente <Icon name="chevron-right" size={14} />
      </button>
    </nav>
  )
}

/* ---------- cards ---------- */

export function Card({ title, actions, children, className, tone, id, footer }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; tone?: string; id?: string; footer?: ReactNode }) {
  const titleId = useId()
  return (
    <section className={cx('card', className)} data-tone={tone} aria-labelledby={title ? titleId : undefined} id={id}>
      {title || actions ? (
        <header className="card__head">
          {title ? <h2 id={titleId} className="card__title">{title}</h2> : <span />}
          {actions ? <div className="card__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="card__body">{children}</div>
      {footer ? <footer className="card__foot">{footer}</footer> : null}
    </section>
  )
}

export interface Crumb {
  label: string
  to?: string
}

/** Drill-down trail: every level but the last is a link; the last one is the current page. */
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Ruta de navegación" className="crumbs">
      <ol>
        {items.map((c, i) => (
          <li key={`${i}-${c.label}`}>
            {c.to && i < items.length - 1 ? <Link to={c.to}>{c.label}</Link> : <span aria-current={i === items.length - 1 ? 'page' : undefined}>{c.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  )
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-head__title">{title}</h1>
        {subtitle ? <p className="page-head__sub muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-head__actions">{actions}</div> : null}
    </div>
  )
}

export function KpiCard({ label, value, caption, tone, onClick, hint }: { label: string; value: ReactNode; caption?: ReactNode; tone: 'brand' | 'blue' | 'green' | 'pink' | 'peri' | 'amber'; onClick?: () => void; hint?: string }) {
  const body = (
    <>
      <span className="kpi__label">{label}</span>
      <span className="kpi__row">
        <span className="kpi__value mono">{value}</span>
        {caption ? <span className="kpi__caption">{caption}</span> : null}
      </span>
    </>
  )
  return onClick ? (
    <button type="button" className="kpi" data-tone={tone} onClick={onClick} title={hint}>{body}</button>
  ) : (
    <div className="kpi" data-tone={tone} title={hint}>{body}</div>
  )
}

/* ---------- forms ---------- */

export function Field({ label, error, hint, children, htmlFor }: { label: string; error?: string; hint?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div className={cx('field', error && 'field--error')}>
      <label htmlFor={htmlFor} className="field__label">{label}</label>
      {children}
      {hint && !error ? <span className="field__hint">{hint}</span> : null}
      {error ? <span className="field__error" role="alert">{error}</span> : null}
    </div>
  )
}

export function Modal({ title, onClose, children, footer, wide }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const titleId = useId()
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }} onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}>
      <div className={cx('modal', wide && 'modal--wide')} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="modal__head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={18} /></button>
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__foot">{footer}</footer> : null}
      </div>
    </div>
  )
}

/* ---------- tabs & search ---------- */

export function Tabs<T extends string>({ label, value, onChange, tabs }: { label: string; value: T; onChange: (v: T) => void; tabs: { value: T; label: string }[] }) {
  const id = useId()
  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    const i = tabs.findIndex((t) => t.value === value)
    if (e.key === 'ArrowRight') { e.preventDefault(); onChange(tabs[(i + 1) % tabs.length].value) }
    if (e.key === 'ArrowLeft') { e.preventDefault(); onChange(tabs[(i - 1 + tabs.length) % tabs.length].value) }
  }
  return (
    <div role="tablist" aria-label={label} className="tabs" onKeyDown={onKey}>
      {tabs.map((t) => (
        <button key={t.value} type="button" role="tab" id={`${id}-${t.value}`} aria-selected={t.value === value} tabIndex={t.value === value ? 0 : -1} className={cx('tabs__tab', t.value === value && 'is-active')} onClick={() => onChange(t.value)}>{t.label}</button>
      ))}
    </div>
  )
}

export function SearchBox({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="searchbox">
      <Icon name="search" size={16} />
      <input type="search" role="searchbox" aria-label={label} placeholder={placeholder ?? label} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}
